import React from "react";
import MenuSelect from "../../ui/MenuSelect";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../../ui/Collapsible";
import {
  CONE_OPTIONS,
  JACOBIAN_OPTIONS,
  PanelNumberInput,
  Row,
  SOLVER_OPTIONS,
  clampedDecimal,
  numberOrFallback,
} from "./PhysicsPanelControls";

export default function PhysicsSimulationOptionsSection({
  isAuthoringLocked,
  sceneOption,
  updateSceneOption,
}) {
  return (
    <>
      <Collapsible defaultOpen>
        <CollapsibleTrigger quiet className="w-full text-xs text-gray-200 justify-between">
          Simulation Options
        </CollapsibleTrigger>
        <CollapsibleContent>
          <Row label="Time Step">
            <PanelNumberInput
              label="Time Step"
              step={0.001}
              min={0.0002}
              max={0.02}
              className="flex-1"
              value={sceneOption.timestep}
              disabled={isAuthoringLocked}
              onChange={(e) => {
                const v = Math.max(0.0002, Math.min(0.02, Number(e.target.value) || 0.002));
                updateSceneOption({ timestep: v });
              }}
            />
            <span className="text-gray-400 text-xs select-none">s/step</span>
          </Row>
          <Row label="Gravity">
            <div className="grid w-full grid-cols-3 gap-1">
              {sceneOption.gravity.map((component, index) => (
                <label key={index} className="min-w-0">
                  <span className="mb-1 block text-[11px] uppercase panel-text-helper select-none">
                    {["X", "Y", "Z"][index]}
                  </span>
                  <PanelNumberInput
                    label={`Gravity ${["X", "Y", "Z"][index]}`}
                    step={0.01}
                    className="w-full"
                    value={component}
                    disabled={isAuthoringLocked}
                    onChange={(e) => {
                      const next = [...sceneOption.gravity];
                      next[index] = numberOrFallback(e.target.value, component);
                      updateSceneOption({ gravity: next });
                    }}
                  />
                </label>
              ))}
            </div>
          </Row>
          <Row label="Integrator">
            <MenuSelect
              size="compact"
              className="w-full min-w-0"
              value={sceneOption.integrator}
              onChange={(value) => updateSceneOption({ integrator: value })}
              disabled={isAuthoringLocked}
              options={[
                { value: "implicitfast", label: "implicitfast" },
                { value: "implicit", label: "implicit" },
                { value: "Euler", label: "Euler" },
                { value: "RK4", label: "RK4" },
              ]}
              triggerProps={{ onPointerDown: (e) => e.stopPropagation() }}
              aria-label="Integrator"
            />
          </Row>
          <Row label="Solver Iterations">
            <PanelNumberInput
              label="Solver Iterations"
              step={1}
              min={1}
              max={2000}
              className="flex-1"
              value={sceneOption.iterations}
              disabled={isAuthoringLocked}
              onChange={(e) => {
                const raw = Number(e.target.value);
                const v = Math.max(1, Math.min(2000, Number.isFinite(raw) ? Math.floor(raw) : 100));
                updateSceneOption({ iterations: v });
              }}
            />
            <span className="text-gray-400 text-xs select-none">iterations</span>
          </Row>
          <Row label="Solver Tolerance">
            <PanelNumberInput
              label="Solver Tolerance"
              step={1e-9}
              min={1e-12}
              max={1e-2}
              className="flex-1"
              value={sceneOption.tolerance}
              disabled={isAuthoringLocked}
              onChange={(e) => {
                updateSceneOption({
                  tolerance: clampedDecimal(e.target.value, 1e-8, 1e-12, 1e-2, 12),
                });
              }}
            />
            <span className="text-gray-400 text-xs select-none">tolerance</span>
          </Row>
          <Row label="Solver">
            <MenuSelect
              size="compact"
              className="w-full min-w-0"
              value={sceneOption.solver}
              onChange={(value) => updateSceneOption({ solver: value })}
              disabled={isAuthoringLocked}
              options={SOLVER_OPTIONS}
              triggerProps={{ onPointerDown: (e) => e.stopPropagation() }}
              aria-label="Solver"
            />
          </Row>
          <Row label="Contact Cone">
            <MenuSelect
              size="compact"
              className="w-full min-w-0"
              value={sceneOption.cone}
              onChange={(value) => updateSceneOption({ cone: value })}
              disabled={isAuthoringLocked}
              options={CONE_OPTIONS}
              triggerProps={{ onPointerDown: (e) => e.stopPropagation() }}
              aria-label="Contact cone"
            />
          </Row>
          <Row label="Jacobian">
            <MenuSelect
              size="compact"
              className="w-full min-w-0"
              value={sceneOption.jacobian}
              onChange={(value) => updateSceneOption({ jacobian: value })}
              disabled={isAuthoringLocked}
              options={JACOBIAN_OPTIONS}
              triggerProps={{ onPointerDown: (e) => e.stopPropagation() }}
              aria-label="Jacobian"
            />
          </Row>
          <Row label="Imp Ratio">
            <PanelNumberInput
              label="Imp Ratio"
              step={0.1}
              min={0.001}
              max={100}
              className="w-full"
              value={sceneOption.impratio}
              disabled={isAuthoringLocked}
              onChange={(e) => {
                updateSceneOption({
                  impratio: clampedDecimal(e.target.value, 1, 0.001, 100, 3),
                });
              }}
            />
          </Row>
        </CollapsibleContent>
      </Collapsible>
    </>
  );
}
